import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useRealtimeEvent, type UserEvent } from '@/features/realtime/RealtimeProvider';

import { sessionsKeys } from './useSessions';

/**
 * App-root listener: a `session.changed` event invalidates the sessions list
 * (all param variants, via the `['sessions','list']` prefix), the affected
 * session's detail, and the dashboard "next session" query. One subscription
 * keeps every sessions surface fresh (My-Sessions, calendar, today-overview,
 * workspace rail, up-next/home card + live-pill, detail).
 *
 * The home "next session" card and hero live-pill read from a dedicated
 * `operation('next-session')` query that lives on a separate key branch from
 * `list`, so the `lists()` prefix above does NOT reach it — it must be
 * invalidated explicitly, otherwise a session that completes (or goes live)
 * keeps showing a stale "Session is Live / Join Now" state on home until the
 * user refreshes. The bare `['sessions','next-session']` prefix matches every
 * client/day variant of that query.
 *
 * No role gate: the backend only delivers this event to the session's own
 * client and practitioner. Events are invalidation signals — never trust the
 * payload as the new entity; always refetch.
 */
export function useSessionChangedListener(): void {
  const queryClient = useQueryClient();
  const handler = useCallback(
    (event: Extract<UserEvent, { type: 'session.changed' }>) => {
      void queryClient.invalidateQueries({ queryKey: sessionsKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: sessionsKeys.detail(event.sessionId) });
      void queryClient.invalidateQueries({ queryKey: sessionsKeys.operation('next-session') });
    },
    [queryClient],
  );
  useRealtimeEvent('session.changed', handler);
}
